import './communications';

function focusAutofocusSearch() {
    window.requestAnimationFrame(() => {
        const target = document.querySelector('[data-autofocus-search]');

        if (!target) {
            return;
        }

        const input = target.matches('input, textarea, select, [contenteditable="true"]')
            ? target
            : target.querySelector('input, textarea, select, [contenteditable="true"]');

        if (!input || input.disabled || input.readOnly || input.offsetParent === null) {
            return;
        }

        input.focus({ preventScroll: true });

        if (typeof input.setSelectionRange === 'function') {
            const length = input.value?.length ?? 0;
            input.setSelectionRange(length, length);
        }
    });
}

function textLabelFrom(element) {
    return element.textContent.trim().replace(/\s+/g, ' ');
}

function tableHeaders(table) {
    return Array.from(table.querySelectorAll(':scope > thead > tr > th, :scope [data-flux-columns] [data-flux-column]'))
        .map(textLabelFrom);
}

function tableRows(table) {
    return Array.from(table.querySelectorAll(':scope > tbody > tr, :scope [data-flux-rows] > [data-flux-row]'));
}

function applyResponsiveTableLabels(root = document) {
    const tables = root.querySelectorAll ? root.querySelectorAll('table') : [];

    tables.forEach((table) => {
        const headers = tableHeaders(table);

        if (!headers.some(Boolean)) {
            table.removeAttribute('data-responsive-table');
            return;
        }

        tableRows(table).forEach((row) => {
            Array.from(row.children).forEach((cell, index) => {
                if (!cell.matches('td, [data-flux-cell]')) {
                    return;
                }

                const colspan = Number.parseInt(cell.getAttribute('colspan') || '1', 10);
                const label = headers[index] || '';

                if (colspan > 1 || !label) {
                    cell.removeAttribute('data-responsive-label');
                    return;
                }

                cell.setAttribute('data-responsive-label', label);
            });
        });

        table.setAttribute('data-responsive-table', 'true');
    });
}

let responsiveTablesQueued = false;

function queueResponsiveTableLabels() {
    if (responsiveTablesQueued) {
        return;
    }

    responsiveTablesQueued = true;

    window.requestAnimationFrame(() => {
        responsiveTablesQueued = false;
        applyResponsiveTableLabels();
    });
}

const nativeActionState = new WeakMap();
const livewireActionState = new WeakMap();

function isActionControl(element) {
    return element instanceof Element
        && element.matches('button, input[type="submit"], input[type="button"], a, [role="button"]');
}

function hasManualLoadingFeedback(control) {
    return control.closest('[data-no-loading-feedback]') !== null
        || control.matches('[data-loading-feedback="manual"]')
        || Array.from(control.querySelectorAll('.animate-spin, [data-loading-indicator]'))
            .some(indicator => !indicator.closest('[data-flux-loading-indicator]'));
}

function rememberAttribute(element, name) {
    return {
        present: element.hasAttribute(name),
        value: element.getAttribute(name),
    };
}

function restoreAttribute(element, name, state) {
    if (state.present) {
        element.setAttribute(name, state.value ?? '');
        return;
    }

    element.removeAttribute(name);
}

function markNativeActionLoading(control, form = null) {
    if (!isActionControl(control) || hasManualLoadingFeedback(control) || nativeActionState.has(control)) {
        return false;
    }

    nativeActionState.set(control, {
        ariaBusy: rememberAttribute(control, 'aria-busy'),
        ariaDisabled: rememberAttribute(control, 'aria-disabled'),
        form,
    });

    control.dataset.actionLoading = 'true';
    control.setAttribute('aria-busy', 'true');
    control.setAttribute('aria-disabled', 'true');

    if (control.closest('[data-action-feedback-labels]')) {
        const status = document.createElement('span');
        status.dataset.nativeActionStatus = 'true';
        status.setAttribute('role', 'status');
        status.textContent = 'Procesando…';
        control.append(status);
    }

    return true;
}

function clearNativeActionLoading(control) {
    control.querySelector('[data-native-action-status]')?.remove();
    const state = nativeActionState.get(control);

    if (!state) {
        control.removeAttribute('data-action-loading');
        return;
    }

    restoreAttribute(control, 'aria-busy', state.ariaBusy);
    restoreAttribute(control, 'aria-disabled', state.ariaDisabled);
    control.removeAttribute('data-action-loading');

    if (state.form) {
        state.form.removeAttribute('data-action-submitting');
    }

    nativeActionState.delete(control);
}

function clearNativeActionStates() {
    document.querySelectorAll('[data-action-loading="true"]').forEach(clearNativeActionLoading);
}

function submitControl(form, submitter) {
    if (isActionControl(submitter)) {
        return submitter;
    }

    return form.querySelector('button[type="submit"], input[type="submit"]');
}

function bootNativeActionFeedback() {
    if (window.__actionFeedbackBooted) {
        return;
    }

    window.__actionFeedbackBooted = true;

    document.addEventListener('submit', (event) => {
        const form = event.target;

        if (!(form instanceof HTMLFormElement) || event.defaultPrevented || form.closest('[data-no-loading-feedback]')) {
            return;
        }

        if (form.dataset.actionSubmitting === 'true') {
            event.preventDefault();
            return;
        }

        const control = submitControl(form, event.submitter);

        if (!control || !markNativeActionLoading(control, form)) {
            return;
        }

        form.dataset.actionSubmitting = 'true';

        // Downloads and interrupted navigations can leave the current document alive.
        window.setTimeout(() => {
            if (document.contains(control)) {
                clearNativeActionLoading(control);
            }
        }, 120000);
    });

    document.addEventListener('click', (event) => {
        const busyControl = event.target instanceof Element
            ? event.target.closest('[data-loading="true"], [data-action-loading="true"]')
            : null;

        if (busyControl) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }

        const explicitControl = event.target instanceof Element
            ? event.target.closest('a[download], a[data-loading-feedback]')
            : null;

        if (!explicitControl || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        if (markNativeActionLoading(explicitControl)) {
            window.setTimeout(() => clearNativeActionLoading(explicitControl), 10000);
        }
    }, true);

    document.addEventListener('keydown', (event) => {
        if (!['Enter', ' '].includes(event.key) || !(event.target instanceof Element)) {
            return;
        }

        if (event.target.closest('[data-loading="true"], [data-action-loading="true"]')) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }
    }, true);

    window.addEventListener('pageshow', clearNativeActionStates);
}

function syncLivewireActionFeedback(control) {
    const loading = control.getAttribute('data-loading') === 'true';

    if (!loading) {
        control.removeAttribute('data-loading-feedback-runtime');

        if (livewireActionState.has(control)) {
            const state = livewireActionState.get(control);
            restoreAttribute(control, 'aria-busy', state.ariaBusy);
            restoreAttribute(control, 'aria-disabled', state.ariaDisabled);
            livewireActionState.delete(control);
        }

        return;
    }

    if (!isActionControl(control)) {
        return;
    }

    if (hasManualLoadingFeedback(control)) {
        control.dataset.loadingFeedbackRuntime = 'manual';
        return;
    }

    if (!livewireActionState.has(control)) {
        livewireActionState.set(control, {
            ariaBusy: rememberAttribute(control, 'aria-busy'),
            ariaDisabled: rememberAttribute(control, 'aria-disabled'),
        });
        control.setAttribute('aria-busy', 'true');
        control.setAttribute('aria-disabled', 'true');
    }
}

function bootLivewireActionObserver() {
    if (!document.body || window.__livewireActionFeedbackObserver) {
        return;
    }

    window.__livewireActionFeedbackObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => syncLivewireActionFeedback(mutation.target));
    });

    window.__livewireActionFeedbackObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['data-loading'],
        subtree: true,
    });

    document.querySelectorAll('[data-loading="true"]').forEach(syncLivewireActionFeedback);
}

function redirectToLoginAfterSessionExpiry() {
    const loginUrl = new URL('/login', window.location.origin);

    window.location.assign(loginUrl.toString());
}

function bootLivewireSessionExpiryRedirect() {
    if (!window.Livewire || window.__livewireSessionExpiryRedirectBooted) {
        return;
    }

    window.__livewireSessionExpiryRedirectBooted = true;

    window.Livewire.hook('request', ({ fail }) => {
        fail(({ status, preventDefault }) => {
            if (status !== 419) {
                return;
            }

            preventDefault();
            redirectToLoginAfterSessionExpiry();
        });
    });
}

function bootResponsiveTableObserver() {
    if (!document.body || window.__adminResponsiveTableObserver) {
        return;
    }

    window.__adminResponsiveTableObserver = new MutationObserver((mutations) => {
        const shouldRefresh = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) {
                return false;
            }

            return node.matches?.('table, [data-flux-table]')
                || node.querySelector?.('table, [data-flux-table]');
        }));

        if (shouldRefresh) {
            queueResponsiveTableLabels();
        }
    });

    window.__adminResponsiveTableObserver.observe(document.body, {
        childList: true,
        subtree: true,
    });
}

function bootAdminUiEnhancements() {
    focusAutofocusSearch();
    applyResponsiveTableLabels();
    bootResponsiveTableObserver();
    bootNativeActionFeedback();
    bootLivewireActionObserver();
    bootLivewireSessionExpiryRedirect();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAdminUiEnhancements, { once: true });
} else {
    bootAdminUiEnhancements();
}

document.addEventListener('livewire:navigated', () => {
    clearNativeActionStates();
    focusAutofocusSearch();
    queueResponsiveTableLabels();
});

document.addEventListener('livewire:init', bootLivewireSessionExpiryRedirect);
